import EngineCleanupBase from '../../../services/engines/EngineCleanupBase';
import ComponentNotifier from '../../../services/wss/ComponentNotifier';
import { EngineNames } from '../../../services/wss/events/EngineNames';
import yourDataCache from './YourDataCache';
import yourDataFetcher from './YourDataFetcher';

import userStateEngine2 from '../../../controllers/UserState/UserStateEngine2';
import { UserStateKeys } from '../../../controllers/UserState/UserStateKeys';

const _name = EngineNames.YourDataEngine;

/**
 * Orchestrates YourData — delegates to handlers and fetcher.
 * Singleton — one instance shared across the app.
 */
class YourDataEngine extends EngineCleanupBase {
    _cache = yourDataCache;

    _selectedTopic = '__recent__';
    _selectedEntry = null;
    _page = 1;
    _total = 0;

    bootup = () => {
        this.initialize(_name);
        this._onUserStateRestored = ComponentNotifier.subscribe(EngineNames.UserStateEngine2, (e) => {
            if (e.target !== 'restored') return;
            this.loadLastSelected();
        });
    };

    /**
     * Restores last selected topic/entry from user state — called on UserStateEngine2 'restored' event.
     */
    loadLastSelected = async () => {
        let topic = userStateEngine2.getValue(UserStateKeys.yourDataActiveTopic);
        if (!topic) {
            topic = '__recent__';
        }

        await this.selectTopic(topic);

        const rk = userStateEngine2.getValue(UserStateKeys.yourDataActiveEntry);
        if (!rk) return;

        const entry = this._cache.get(rk);
        if (!entry) return;

        this.selectEntry(entry);
    };

    /**
     * Fetches page 1 of entries for a topic and updates cache.
     * @param {string} topic
     */
    selectTopic = async (topic) => {
        try {
            if (!topic) return;

            this._selectedTopic = topic;
            this._selectedEntry = null;
            this._page = 1;

            // get the recent entries from BE
            const data = await yourDataFetcher.fetch(topic, 1);
            if (!data) {
                this._notify();
                return;
            }

            this._cache.setCache(data.entries);
            this._total = data.total;
            this._page = data.page;
            this._selectedEntry = data.entries[0] ?? null;

            this._notify();
        } catch (error) {
            this.logError('selectTopic', error.message);
        }
    };

    /**
     * Loads a specific page for the active topic — updates cache and notifies.
     * Resets selected entry to the first entry of the new page, same convention as selectTopic.
     * @param {number} page
     */
    loadPage = async (page) => {
        try {
            if (!page || page < 1) return;

            const data = await yourDataFetcher.fetch(this._selectedTopic, page);
            if (!data) return;

            this._cache.setCache(data.entries);
            this._total = data.total;
            this._page = data.page;
            this._selectedEntry = data.entries[0] ?? null;

            this._notify();
        } catch (error) {
            this.logError('loadPage', error.message);
        }
    };

    /**
     * Sets the selected entry and notifies listeners.
     * @param {object|null} entry
     */
    selectEntry = (entry) => {
        try {
            this._selectedEntry = entry;
            userStateEngine2.set([{ name: UserStateKeys.yourDataActiveEntry, value: entry?.rk ?? null }]);

            this._notify();
        } catch (error) {
            this.logError('selectEntry', error.message);
        }
    };

    /**
     * Called directly from PageContentSRHandlers on OnJournalUpdated.
     * @param {object[]} data
     */
    onJournalUpdated = (data) => {
        try {
            if (!Array.isArray(data) || !data.length) {
                const msg = `$handle: Entries is not an array or empty`;
                this.logError('onJournalUpdated', msg);
                return false;
            }

            // update cache with updates
            const changed = this._cache.addOrUpdate(data);
            if (!changed) {
                return;
            }

            // update selected if it was in data
            const updated = data.find((x) => x.rk === this._selectedEntry?.rk);
            if (updated) {
                this._selectedEntry = updated;
            }

            // notify paint hooks
            this._notify();
        } catch (error) {
            this.logError('onJournalUpdated', error.message);
        }
    };

    /**
     * Called directly from PageContentSRHandlers on OnJournalDeleted.
     * @param {string} rk
     */
    onNoteDeleted = (rk) => {
        try {
            if (!rk) {
                this.logError('onNoteDeleted', `Invalid row key - ${rk}`);
                return false;
            }

            // remove it from cache
            const deleted = this._cache.delete(rk);

            if (this._selectedEntry?.rk === rk) {
                this._selectedEntry = null;
            }

            this._notify();

            return deleted;
        } catch (error) {
            this.logError('onNoteDeleted', error.message);
            return false;
        }
    };

    getEntries = () => {
        // TODO: this isn't right what about pages ?
        return this._cache.getAll();
    };

    getSelectedTopic = () => this._selectedTopic;
    getSelectedEntry = () => this._selectedEntry;
    getTotal = () => this._total;
    getCurrentPage = () => this._page;

    clear = () => {
        if (this._onUserStateRestored) this._onUserStateRestored = this._onUserStateRestored();
        this._cache.clear();
        this._notify();
        super.clear();
    };

    // ─── private ──────────────────────────────────────────

    _notify = () => {
        ComponentNotifier.notify(_name, 'dataChanged', {
            entries: this.getEntries(),
            selectedTopic: this.getSelectedTopic(),
            selectedEntry: this.getSelectedEntry(),
            total: this.getTotal(),
            currentPage: this.getCurrentPage(),
        });
    };
}

const yourDataEngine = new YourDataEngine();
export default yourDataEngine;
